import { WButton } from "@/components/button/WButton.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { useStandardMutation, useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { AddIpForm } from "@/modules/services/AddIpForm.tsx";
import type { ManagedServiceWithAssigned } from "@/modules/services/joinAssignedQuery.ts";
import { deleteServiceWhitelistAddressMutation, getServiceWhitelistQuery } from "@/modules/services/servicesQuery.ts";
import { Trans } from "@lingui/react/macro";
import { useState } from "react";

export function IpWhitelistContent({ item }: { item: ManagedServiceWithAssigned }) {
    const { uuid } = item;

    //#region Hooks
    const whitelist = useStandardSuspenseQuery(getServiceWhitelistQuery, { uuid });
    const deleteWhitelistAddressMutation = useStandardMutation(deleteServiceWhitelistAddressMutation);
    const [isVisible, setIsVisible] = useState(false);
    //#endregion

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader
                title={<Trans>IP allowlist</Trans>}
                description={
                    <Trans>
                        To restrict connections to the service you have to add at least one inbound IP to the allowlist
                        (All other public and private connections will be denied). You can specify a single IPv4 address
                        or a block of addresses using CIDR notation (e.g. 1.1.1.0/24)
                    </Trans>
                }
            />

            <WTable
                afterTable={
                    !isVisible && (
                        <WButton
                            color="primary"
                            size="bar"
                            variant="border"
                            icon="jp-icon-add"
                            action={() => setIsVisible(!isVisible)}
                        >
                            <Trans>Add IP</Trans>
                        </WButton>
                    )
                }
            >
                <thead>
                    <tr>
                        <th>
                            <Trans>IP</Trans>
                        </th>
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {whitelist.data.map((address) => (
                        <tr key={address}>
                            <td>
                                <ClipBoardTooltip>{address}</ClipBoardTooltip>
                            </td>
                            <td className="text-right">
                                <DeleteModal
                                    inTable
                                    modalAction={async () => {
                                        await deleteWhitelistAddressMutation.mutateAsync({
                                            uuid,
                                            body: { ip_address: address },
                                        });
                                    }}
                                >
                                    <Trans>
                                        This will remove the address or range from the allowlist.
                                        <br />
                                        Note that an empty allowlist will permit connections from any address.
                                    </Trans>
                                </DeleteModal>
                            </td>
                        </tr>
                    ))}
                </WTableBody>

                {isVisible && (
                    <tfoot>
                        <tr>
                            <td colSpan={4}>
                                <AddIpForm obj={item} onClose={() => setIsVisible(false)} />
                            </td>
                        </tr>
                    </tfoot>
                )}
            </WTable>
        </ContentPane>
    );
}
